import { useState } from 'react';
import { PACKAGES } from '@aslgo/shared';
import { SESSION_DURATIONS } from './DurationSelector.js';
import { useAppStore } from '../store/useAppStore.js';
import { usePackagePurchase } from '../hooks/usePackagePurchase.js';
import { PurchaseModal } from './PurchaseModal.js';
import { ConfirmPurchaseDialog } from './ConfirmPurchaseDialog.js';
import { EnterpriseContactRow } from './EnterpriseContactRow.js';

/** Extend prompt shown to the learner near the end of the session. Rendered as an
 *  inline panel (in the chat rail under the balance pill on desktop, or a top
 *  sheet on mobile) — not a center overlay. Pick a block; if the balance is short,
 *  buy a package first. The pick is handed up to CallView, which requests the
 *  guide's approval over the data channel. */
export function ExtendSessionModal({ onPickBlock, onClose }: { onPickBlock: (minutes: number) => void; onClose: () => void }) {
  const sessionMinutes = useAppStore((s) => s.sessionMinutes);
  const setSessionMinutes = useAppStore((s) => s.setSessionMinutes);
  const [block, setBlock] = useState<number | null>(null);
  const [selectedPkg, setSelectedPkg] = useState<string | null>(null);
  const {
    card, submitting: buying, error: err, purchase, buy, onPurchased, closePurchase,
    pendingBuy, confirmBuy, cancelBuy,
  } = usePackagePurchase(setSessionMinutes);
  const short = block != null && sessionMinutes < block;

  async function onBuy() {
    if (!selectedPkg) return;
    const pkg = PACKAGES.find((p) => p.id === selectedPkg)!;
    await buy(pkg);
    setSelectedPkg(null);
  }

  return (
    <div className="card p-4 text-ink-900 dark:text-ink-100">
      <h2 className="font-semibold text-[1rem] mb-0.5">Session ending soon</h2>
      <p className="hint mb-3">Extend it — pick how much more time.</p>
      <div className="grid grid-cols-4 gap-2 mb-3">
        {SESSION_DURATIONS.map((n) => (
          <button key={n} type="button" onClick={() => setBlock(n)}
            className={`px-2 py-2 rounded-lg text-sm font-medium border ${block === n ? 'border-brand ring-1 ring-brand text-ink-900 dark:text-ink-100' : 'border-ink-300 dark:border-white/10 text-ink-700 dark:text-ink-300'}`}>
            {n} min
          </button>
        ))}
      </div>
      {short ? (
        <div className="mb-3">
          <p className="text-[0.85rem] text-status-error dark:text-red-400 mb-2">Not enough minutes for {block} more — buy a package:</p>
          <div className="flex flex-col gap-1.5 max-h-52 overflow-y-auto">
            {PACKAGES.map((p) => (
              <button key={p.id} type="button" disabled={buying} onClick={() => setSelectedPkg(p.id)}
                className={`flex items-center justify-between gap-2 rounded-lg border p-2.5 text-left hover:border-brand/60 ${selectedPkg === p.id ? 'border-brand ring-1 ring-brand' : 'border-ink-200 dark:border-white/10'}`}>
                <span><span className="font-semibold">{p.name}</span><span className="block text-[0.75rem] text-ink-500">{p.credits} cr · {p.minutes} min</span></span>
                <span className="font-semibold">${p.priceDollars.toLocaleString()}</span>
              </button>
            ))}
            <EnterpriseContactRow compact />
          </div>
          <button type="button" className="btn-primary w-full justify-center mt-2" disabled={!selectedPkg || buying}
            onClick={() => { void onBuy(); }}>
            {buying ? 'Buying…' : 'Buy package'}
          </button>
        </div>
      ) : null}
      {err ? <p className="text-status-error text-sm mb-2">{err}</p> : null}
      <div className="flex gap-2 justify-end">
        <button type="button" className="btn" onClick={onClose}>Not now</button>
        <button type="button" className="btn-primary" disabled={block == null || short}
          onClick={() => { if (block != null) onPickBlock(block); }}>
          {block ? `Request ${block} more` : 'Request more'}
        </button>
      </div>
      {purchase ? (
        <PurchaseModal clientSecret={purchase.clientSecret} onPurchased={onPurchased} onClose={closePurchase} />
      ) : null}
      {pendingBuy && card ? (
        <ConfirmPurchaseDialog pkg={pendingBuy} card={card} busy={buying} onConfirm={confirmBuy} onCancel={cancelBuy} />
      ) : null}
    </div>
  );
}
